<template>
  <div class="media-grid-container">
    <!-- Toolbar slot -->
    <div v-if="$slots.toolbar" class="media-grid-toolbar">
      <slot name="toolbar" :selected="selectedItems" :select-all="selectAll" :clear-selection="clearSelection"/>
    </div>

    <!-- Loading state -->
    <div v-if="loading && items.length === 0" class="media-grid-loading">
      <CSpinner color="primary" size="sm"/>
      <span class="ml-2 text-muted">
        <slot name="loading-text">
          <translate translate-context="core.mediaGrid">Loading...</translate>
        </slot>
      </span>
    </div>

    <!-- Empty state -->
    <div v-else-if="items.length === 0" class="media-grid-empty">
      <slot name="empty">
        <CIcon name="cil-image" size="xl" class="text-muted mb-2"/>
        <p class="text-muted mb-0">
          <translate translate-context="core.mediaGrid">No photos found</translate>
        </p>
      </slot>
    </div>

    <!-- Grid content -->
    <template v-else>
      <div
        class="media-grid-wrapper"
        :class="{
          'is-expanded': isExpanded,
          'is-collapsible': collapsible && canCollapse
        }"
        :style="wrapperStyle"
      >
        <!-- Grid -->
        <div class="media-grid" ref="grid">
          <div
            v-for="item in displayedItems"
            :key="item.id"
            class="media-grid-item"
            :class="{
              'is-selected': isSelected(item.id),
              'is-hovering': isHovering(item.id)
            }"
            @click="onItemClick($event, item)"
            @contextmenu.prevent="onContextMenu($event, item)"
            @mouseenter="onMouseEnter(item.id)"
            @mouseleave="onMouseLeave"
          >
            <!-- Selection checkbox -->
            <div v-if="isSelectable" class="media-grid-checkbox" @click.stop="toggleSelection(item.id)">
              <CIcon
                name="cipCheckbox"
                size="custom-size"
                :width="20"
                :class="['checkbox-icon', { 'is-selected': isSelected(item.id) }]"
              />
            </div>

            <!-- Custom item slot or default thumbnail -->
            <slot name="item" :item="item" :selected="isSelected(item.id)">
              <img
                :src="item.thumb || item.thumbnail"
                :alt="item.id"
                class="media-grid-image"
                @error="onImageError"
                @dragstart.prevent
              />
            </slot>

            <!-- Zoom button -->
            <div
              v-if="zoomable"
              class="media-grid-zoom"
              @click.stop="onZoom(item)"
            >
              <CIcon name="cil-zoom-in" size="sm"/>
            </div>

            <!-- Item overlay slot (badges, etc) -->
            <slot name="item-overlay" :item="item"/>
          </div>
        </div>

        <!-- Fade overlay when collapsed -->
        <div v-if="collapsible && canCollapse && !isExpanded" class="media-grid-fade"/>

        <!-- Expand button -->
        <div v-if="collapsible && canCollapse && !isExpanded" class="media-grid-expand">
          <CButton color="primary" size="sm" class="shadow" @click="expand">
            <CIcon name="cipChevronDown" size="sm" class="mr-1"/>
            <translate
              translate-context="core.mediaGrid"
              :translate-params="{ count: totalCount }"
            >
              Show all %{count} photos
            </translate>
          </CButton>
        </div>

        <!-- Collapse button -->
        <div v-if="collapsible && isExpanded && canCollapse" class="media-grid-collapse">
          <CButton color="primary" size="sm" @click="collapse">
            <CIcon name="cipChevronUp" size="sm" class="mr-1"/>
            <translate translate-context="core.mediaGrid">Show less</translate>
          </CButton>
        </div>

        <!-- Load more (infinite scroll trigger) -->
        <div v-if="infiniteScroll && hasMore && isExpanded" class="media-grid-loadmore" ref="loadMoreTrigger">
          <CSpinner v-if="loading" color="primary" size="sm"/>
          <CButton v-else color="link" size="sm" @click="loadMore">
            <translate translate-context="core.mediaGrid">Load more</translate>
          </CButton>
        </div>
      </div>
    </template>

    <!-- Selection bar -->
    <transition name="slide-up">
      <div v-if="isSelectable && selectedItems.length > 0" class="media-grid-selection-bar">
        <div class="selection-info">
          <translate
            translate-context="core.mediaGrid"
            :translate-params="{ count: selectedItems.length }"
          >
            %{count} selected
          </translate>
        </div>
        <div class="selection-actions">
          <slot name="selection-actions" :selected="selectedItems" :clear="clearSelection">
            <CButton color="secondary" size="sm" variant="ghost" @click="clearSelection">
              <translate translate-context="core.mediaGrid">Clear</translate>
            </CButton>
          </slot>
        </div>
      </div>
    </transition>

    <!-- Context Menu -->
    <PContextMenu
      ref="contextMenu"
      :menu-items="contextMenuItems"
      @item-selected="onContextAction"
    />

    <!-- Zoom Modal -->
    <CModal :show.sync="zoomOpen" size="xl" @close="closeZoom">
      <template #header>
        <h5 class="modal-title" v-if="zoomItem">
          <CIcon name="cipImage" class="mr-2"/>
          <span v-if="zoomItem.file_name">{{ zoomItem.file_name }}</span>
          <span class="ml-2 text-muted font-weight-normal">[ID {{ zoomItem.id }}]</span>
        </h5>
      </template>

      <div v-if="zoomItem" class="image-container">
        <transition :name="zoomTransition" mode="out-in">
          <img
            :key="zoomItem.id"
            :src="zoomItem.original || zoomItem.hd || zoomItem.sd || zoomItem.thumb"
            :alt="`Photo ${zoomItem.id}`"
            class="modal-preview-image"
            @error="onZoomImageError"
          />
        </transition>
      </div>

      <CLink
        v-if="zoomHasPrev"
        class="nav-photo nav-photo--prev"
        @click="zoomNavigate('prev')"
      >
        <CIcon name="cipChevronLeft" size="custom-size" :width="36"/>
      </CLink>
      <CLink
        v-if="zoomHasNext"
        class="nav-photo nav-photo--next"
        @click="zoomNavigate('next')"
      >
        <CIcon name="cipChevronRight" size="custom-size" :width="36"/>
      </CLink>

      <template #footer>
        <CButton color="secondary" @click="closeZoom">
          <translate translate-context="core.mediaGrid">Close</translate>
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import PContextMenu from '@/domain/core/components/PContextMenu.vue'

export default {
  name: 'MediaGrid',
  components: {
    PContextMenu
  },
  props: {
    // Data
    items: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: null
      // { total, page, num_pages, has_next }
    },
    loading: {
      type: Boolean,
      default: false
    },

    // Behavior
    selectable: {
      type: Boolean,
      default: true
    },
    zoomable: {
      type: Boolean,
      default: true
    },
    collapsible: {
      type: Boolean,
      default: true
    },
    collapsedRows: {
      type: Number,
      default: 2
    },
    infiniteScroll: {
      type: Boolean,
      default: false
    },

    // Context menu
    contextActions: {
      type: Array,
      default: () => []
      // [{ value: 'download', label: 'Download', icon: 'cil-cloud-download' }]
    },

    // Selection state (v-model:selected)
    selected: {
      type: Array,
      default: () => []
    }
  },

  emits: [
    'update:selected',
    'action',
    'zoom',
    'load-more'
  ],

  data() {
    return {
      isExpanded: false,
      itemsPerRow: 5,
      shiftPressed: false,
      lastSelectedId: null,
      hoveringIds: {},
      intersectionObserver: null,
      zoomOpen: false,
      zoomItem: null,
      zoomTransition: ''
    }
  },

  computed: {
    isSelectable() {
      return this.selectable && this.contextActions.length > 0
    },

    selectedItems() {
      return this.selected
    },

    totalCount() {
      return this.pagination?.total || this.items.length
    },

    hasMore() {
      return this.pagination?.has_next || false
    },

    canCollapse() {
      const maxVisible = this.itemsPerRow * this.collapsedRows
      return this.totalCount > maxVisible
    },

    zoomIndex() {
      if (!this.zoomItem) return -1
      return this.items.findIndex(i => i.id === this.zoomItem.id)
    },

    zoomHasPrev() {
      return this.zoomIndex > 0
    },

    zoomHasNext() {
      return this.zoomIndex >= 0 && this.zoomIndex < this.items.length - 1
    },

    displayedItems() {
      if (!this.collapsible || this.isExpanded) {
        return this.items
      }
      const maxVisible = this.itemsPerRow * this.collapsedRows
      return this.items.slice(0, maxVisible)
    },

    wrapperStyle() {
      if (!this.collapsible || this.isExpanded) {
        return {}
      }
      // Calculate max height for collapsed state
      const itemHeight = 150 // approximate
      const gap = 10
      const maxHeight = (itemHeight + gap) * this.collapsedRows + 40
      return { maxHeight: `${maxHeight}px` }
    },

    contextMenuItems() {
      return this.contextActions
    }
  },

  watch: {
    items() {
      this.$nextTick(() => {
        this.calculateItemsPerRow()
      })
    }
  },

  mounted() {
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)
    window.addEventListener('resize', this.calculateItemsPerRow)

    this.$nextTick(() => {
      this.calculateItemsPerRow()
      this.setupIntersectionObserver()
    })
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
    window.removeEventListener('resize', this.calculateItemsPerRow)
    document.removeEventListener('keydown', this.onZoomKeydown)

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
    }
  },

  methods: {
    // Selection
    isSelected(id) {
      return this.selected.includes(id)
    },

    isHovering(id) {
      return this.hoveringIds[id] === true
    },

    toggleSelection(id) {
      const newSelected = [...this.selected]
      const index = newSelected.indexOf(id)

      if (index === -1) {
        newSelected.push(id)
      } else {
        newSelected.splice(index, 1)
      }

      this.$emit('update:selected', newSelected)
      this.lastSelectedId = id
    },

    selectRange(fromId, toId) {
      const fromIndex = this.items.findIndex(i => i.id === fromId)
      const toIndex = this.items.findIndex(i => i.id === toId)

      if (fromIndex === -1 || toIndex === -1) return

      const start = Math.min(fromIndex, toIndex)
      const end = Math.max(fromIndex, toIndex)

      const rangeIds = this.items.slice(start, end + 1).map(i => i.id)
      const newSelected = [...new Set([...this.selected, ...rangeIds])]

      this.$emit('update:selected', newSelected)
    },

    selectAll() {
      const allIds = this.items.map(i => i.id)
      this.$emit('update:selected', allIds)
    },

    clearSelection() {
      this.$emit('update:selected', [])
      this.lastSelectedId = null
    },

    // Click handlers
    onItemClick(event, item) {
      if (!this.isSelectable) {
        this.onZoom(item)
        return
      }

      if (event.shiftKey && this.lastSelectedId) {
        this.selectRange(this.lastSelectedId, item.id)
      } else {
        this.toggleSelection(item.id)
      }
    },

    onMouseEnter(id) {
      if (!this.shiftPressed || !this.lastSelectedId) {
        this.hoveringIds = {}
        return
      }

      const lastIndex = this.items.findIndex(i => i.id === this.lastSelectedId)
      const currentIndex = this.items.findIndex(i => i.id === id)

      if (lastIndex === -1 || currentIndex === -1) {
        this.hoveringIds = {}
        return
      }

      const start = Math.min(lastIndex, currentIndex)
      const end = Math.max(lastIndex, currentIndex)

      const newHovering = {}
      for (let i = start; i <= end; i++) {
        newHovering[this.items[i].id] = true
      }
      this.hoveringIds = newHovering
    },

    onMouseLeave() {
      // Keep hovering state while shift is pressed
    },

    // Keyboard
    onKeyDown(event) {
      if (event.key === 'Shift') {
        this.shiftPressed = true
      }
    },

    onKeyUp(event) {
      if (event.key === 'Shift') {
        this.shiftPressed = false
        this.hoveringIds = {}
      }
    },

    // Context menu
    onContextMenu(event, item) {
      if (!this.isSelectable) return

      // Select item if not already selected
      if (!this.isSelected(item.id)) {
        this.$emit('update:selected', [item.id])
      }

      this.$nextTick(() => {
        this.$refs.contextMenu.open(event)
      })
    },

    onContextAction(action) {
      this.$emit('action', {
        action,
        items: this.items.filter(i => this.selected.includes(i.id))
      })
    },

    // Zoom
    onZoom(item) {
      this.$emit('zoom', item)
      this.zoomItem = item
      this.zoomOpen = true
      document.addEventListener('keydown', this.onZoomKeydown)
    },

    closeZoom() {
      this.zoomOpen = false
      this.zoomItem = null
      this.zoomTransition = ''
      document.removeEventListener('keydown', this.onZoomKeydown)
    },

    zoomNavigate(direction) {
      const idx = this.zoomIndex
      if (idx === -1) return

      if (direction === 'prev' && this.zoomHasPrev) {
        this.zoomTransition = 'slide-horizontal-left'
        this.zoomItem = this.items[idx - 1]
      } else if (direction === 'next' && this.zoomHasNext) {
        this.zoomTransition = 'slide-horizontal-right'
        this.zoomItem = this.items[idx + 1]
      }
    },

    onZoomKeydown(event) {
      if (event.key === 'ArrowLeft') {
        this.zoomNavigate('prev')
      } else if (event.key === 'ArrowRight') {
        this.zoomNavigate('next')
      } else if (event.key === 'Escape') {
        this.closeZoom()
      }
    },

    onZoomImageError(event) {
      if (event.target.dataset.errored) return
      event.target.dataset.errored = 'true'
      if (this.zoomItem) {
        // Fallback: try lower quality
        if (event.target.src !== this.zoomItem.thumb) {
          event.target.src = this.zoomItem.thumb
        } else {
          event.target.src = '/img/placeholder-image.svg'
        }
      }
    },

    // Expand/Collapse
    expand() {
      this.isExpanded = true
      if (this.hasMore) {
        this.$emit('load-more')
      }
    },

    collapse() {
      this.isExpanded = false
    },

    // Load more
    loadMore() {
      this.$emit('load-more')
    },

    // Utilities
    calculateItemsPerRow() {
      if (!this.$refs.grid) return

      const containerWidth = this.$refs.grid.offsetWidth
      const gap = 10
      let itemWidth = 150

      if (window.innerWidth > 1200) {
        itemWidth = 160
      } else if (window.innerWidth < 768) {
        itemWidth = 120
      } else if (window.innerWidth < 576) {
        itemWidth = 100
      }

      this.itemsPerRow = Math.max(3, Math.floor((containerWidth + gap) / (itemWidth + gap)))
    },

    setupIntersectionObserver() {
      if (!this.infiniteScroll) return

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && this.hasMore && !this.loading) {
            this.loadMore()
          }
        },
        { threshold: 0.1 }
      )

      this.$watch(
        () => this.$refs.loadMoreTrigger,
        (el) => {
          if (el) {
            this.intersectionObserver.observe(el)
          }
        },
        { immediate: true }
      )
    },

    onImageError(event) {
      if (event.target.dataset.errored) return
      event.target.dataset.errored = 'true'
      event.target.src = '/img/placeholder-image.svg'
    }
  }
}
</script>

<style scoped>
.media-grid-container {
  position: relative;
}

.media-grid-toolbar {
  margin-bottom: 1rem;
}

.media-grid-loading,
.media-grid-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
}

.media-grid-wrapper {
  position: relative;
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.media-grid-wrapper.is-expanded {
  max-height: none !important;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
}

@media (min-width: 1200px) {
  .media-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
}

@media (max-width: 768px) {
  .media-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
}

@media (max-width: 576px) {
  .media-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
}

.media-grid-item {
  position: relative;
  aspect-ratio: 1;
  border: 2px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
  background: #f8f9fa;
  cursor: pointer;
  transition: all 0.2s;
}

.media-grid-item:hover {
  border-color: #007bff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.media-grid-item.is-selected {
  border-color: #007bff;
  background: #e7f3ff;
  transform: scale(0.95);
}

.media-grid-item.is-hovering {
  border-color: #007bff;
  background: #cce5ff;
  opacity: 0.7;
  transform: scale(0.97);
}

.media-grid-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.media-grid-checkbox {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 10;
  display: none;
  cursor: pointer;
}

.media-grid-item:hover .media-grid-checkbox,
.media-grid-item.is-selected .media-grid-checkbox {
  display: block;
}

@media (max-width: 992px) {
  .media-grid-checkbox {
    display: block !important;
  }
}

.checkbox-icon {
  display: block;
  transition: all 0.2s;
}

.checkbox-icon .box-check {
  stroke: #333;
  fill: rgba(255, 255, 255, 0.9);
}

.checkbox-icon .line-check {
  display: none;
}

.checkbox-icon.is-selected .box-check {
  fill: #007bff;
  stroke: #007bff;
}

.checkbox-icon.is-selected .line-check {
  display: block;
}

.media-grid-zoom {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 2;
  opacity: 0;
}

.media-grid-item:hover .media-grid-zoom {
  opacity: 1;
}

.media-grid-zoom:hover {
  background: white;
  transform: scale(1.05);
}

.media-grid-fade {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100px;
  background: linear-gradient(transparent, rgba(255, 255, 255, 0.95));
  pointer-events: none;
  z-index: 5;
}

.media-grid-expand {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}

.media-grid-collapse {
  display: flex;
  justify-content: center;
  padding: 1rem 0;
}

.media-grid-loadmore {
  display: flex;
  justify-content: center;
  padding: 1rem 0;
}

.media-grid-selection-bar {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  background: #323232;
  color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
}

.selection-info {
  font-weight: 500;
}

.selection-actions {
  display: flex;
  gap: 0.5rem;
}

/* Transitions */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter,
.slide-up-leave-to {
  opacity: 0;
  transform: translate(-50%, 20px);
}
</style>
